import { useState } from "react";
import { Copy } from "lucide-react";
import type { Role } from "@amber/contracts";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  RadioCards,
  ReasonField,
  TextField,
  copyText,
  formatDate,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import { accessCommands } from "../queries";
import { AFTER_ACCESS_CHANGE } from "../keys";
import { ROLE_OPTIONS } from "./shared";

export function InviteDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [note, setNote] = useState("");
  const [created, setCreated] = useState<{ link: string; expires_at: string; email: string } | null>(null);
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      accessCommands.invite({ email: email.trim(), role, note: note.trim() || null, reason: text }, csrf),
    invalidate: AFTER_ACCESS_CHANGE,
    onSuccess: (result) => {
      setCreated({ link: result.link, expires_at: result.expires_at, email: email.trim() });
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const emailMissing = reason.submitted && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  if (created) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title="Invitation created"
        footer={
          <>
            <Button onClick={onClose}>Done</Button>
            <Button
              variant="primary"
              onClick={() => {
                void copyText(created.link).then((ok) => {
                  toast.show(ok ? "Invitation link copied" : "Couldn't copy. Select the link and copy it.");
                });
              }}
            >
              <Copy aria-hidden="true" />
              Copy invitation link
            </Button>
          </>
        }
      >
        <DialogText>
          Share this link with <b>{created.email}</b>. The workspace sends no email. They sign in with Google using that
          address. The link works until {formatDate(created.expires_at)}.
        </DialogText>
        <TextField
          label="Invitation link"
          value={created.link}
          readOnly
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          className="font-mono text-md"
        />
      </Dialog>
    );
  }

  const submit = () => {
    if (reason.submit() && !emailMissing && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      mutation.mutate(reason.trimmed);
  };
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Invite someone"
      onSubmit={submit}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm="Create invitation link"
          pending={mutation.isPending}
          failed={mutation.isError}
        />
      }
    >
      <DialogText>
        You get a link to copy and share. The invitation expires after 14 days. People who already have an account keep
        it; change their role instead.
      </DialogText>
      <TextField
        label="Google address"
        required
        type="email"
        autoComplete="off"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
        }}
        error={fieldErrors["email"] ?? (emailMissing ? "Enter the person's Google Workspace address" : null)}
      />
      <RadioCards
        label="Role"
        value={role}
        options={ROLE_OPTIONS}
        onChange={setRole}
        error={fieldErrors["role"] ?? null}
      />
      <TextField
        label="Note"
        optional="optional, shown with the invitation"
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
        error={fieldErrors["note"] ?? null}
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}
